/**
 * ForceLogoutModal Component
 *
 * Displays a non-dismissable modal when users are being force logged out.
 * Features:
 * - Cannot be closed by clicking outside or pressing escape
 * - Auto-logout countdown (default 10 seconds)
 * - Text countdown display
 * - Stores logout flag in sessionStorage to prevent bypass on page reload
 */

import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import Button, { ButtonColorTypes } from "./Button";

/** Session storage key for the force logout flag */
export const FORCE_LOGOUT_FLAG = "force_logout";

export interface ForceLogoutModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The logout message to display */
  message: string;
  /** Countdown duration in seconds (default: 10) */
  countdownSeconds?: number;
  /** Optional callback after logout completes */
  onLogoutComplete?: () => void;
}

export const ForceLogoutModal = ({
  open,
  message,
  countdownSeconds = 10,
  onLogoutComplete,
}: ForceLogoutModalProps) => {
  const { logout } = useAuth0();
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Track if logout has been automatically triggered to prevent infinite retry loop
  const hasAutoLoggedOut = useRef(false);

  const handleLogout = useCallback(
    (isManual: boolean = false) => {
      setIsLoggingOut(true);

      // Track automatic logout attempts
      if (!isManual) {
        hasAutoLoggedOut.current = true;
      }

      // Store current location to detect if redirect succeeded
      const currentLocation = window.location.href;

      // Clear the force logout flag before redirecting
      // If logout succeeds, the flag will be cleared and we'll redirect
      // If logout fails, we'll restore it below so the modal reappears on refresh
      sessionStorage.removeItem(FORCE_LOGOUT_FLAG);

      // Perform logout
      logout({ returnTo: window.location.origin });

      // Reset isLoggingOut after a short delay in case navigation is blocked
      // This allows the button to be clicked again if logout fails
      // Applies to both manual and automatic logouts
      setTimeout(() => {
        // Check if we're still on the same page (logout failed)
        const stillHere = window.location.href === currentLocation;

        setIsLoggingOut(false);

        // If logout failed (we're still here), restore the flag so modal reappears on refresh
        // If logout succeeded, we'll be redirected and this code won't execute
        if (stillHere) {
          sessionStorage.setItem(FORCE_LOGOUT_FLAG, "true");
        }
      }, 1000);

      if (onLogoutComplete) {
        onLogoutComplete();
      }
    },
    [logout, onLogoutComplete]
  );

  // Play force logout alert sound when modal opens
  const playLogoutAlertSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      // Create a more urgent, alerting sound with two descending tones
      const playTone = (
        frequency: number,
        startTime: number,
        duration: number
      ) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.frequency.linearRampToValueAtTime(
          frequency * 0.7,
          startTime + duration
        );
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.1, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.005, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      // Play two descending tones for urgency
      playTone(700, audioContext.currentTime, 0.2);
      playTone(600, audioContext.currentTime + 0.25, 0.2);
    } catch (error) {
      console.debug("Could not play logout alert sound:", error);
    }
  }, []);

  // Set the force logout flag when modal opens
  useEffect(() => {
    if (open) {
      sessionStorage.setItem(FORCE_LOGOUT_FLAG, "true");
      setCountdown(countdownSeconds);
      setIsLoggingOut(false);
      hasAutoLoggedOut.current = false; // Reset auto-logout flag when modal opens
      playLogoutAlertSound();
    }
  }, [open, countdownSeconds, playLogoutAlertSound]);

  // Countdown timer
  useEffect(() => {
    if (!open || isLoggingOut || hasAutoLoggedOut.current) return;

    if (countdown <= 0) {
      handleLogout(false); // false = automatic logout
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [open, countdown, isLoggingOut, handleLogout]);

  return (
    <Transition appear show={open} as={Fragment}>
      {/* Non-dismissable dialog - onClose is a no-op */}
      <Dialog as="div" className="relative z-[100]" onClose={() => {}}>
        {/* Backdrop - darker and more prominent */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Logout Icon and Title */}
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                      <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900"
                    >
                      System Maintenance and Update
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">{message}</p>
                      {/* Countdown text below message */}
                      <p className="text-sm text-gray-500 mt-2 italic">
                        {isLoggingOut
                          ? "Logging out..."
                          : `Logging out in ${countdown} second${
                              countdown !== 1 ? "s" : ""
                            }...`}
                      </p>
                    </div>
                    {/* Logout now button centered below message text */}
                    <div className="flex justify-center mt-4">
                      <Button
                        text={isLoggingOut ? "Logging out..." : "Logout Now"}
                        colorType={ButtonColorTypes.secondary}
                        onClick={() => handleLogout(true)} // true = manual logout
                        disabled={isLoggingOut}
                      />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ForceLogoutModal;
